import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { merge } from '@tragfaehigkeit/core';
import { de } from '../../lib/i18n/de';
import { useWizardStore } from '../../lib/store/wizard';
import type { DimensionKey } from '../hub/dimensions';
import { BeraterDimensionReview, type BeraterReviewField } from '../wizard/BeraterDimensionReview';
import {
  IDENTITY_KEYS,
  ACTIVITY_KEYS,
  STYLE_KEYS,
  HEALTH_KEYS,
  PRIVATE_KEYS,
} from '../../lib/store/dimensionScoreInput';

/**
 * Cockpit-Sektion: fachliche Bearbeitung der Freitext-Dimensionen im
 * Zwei-Ebenen-Modell. Zeigt pro Dimension die Mandanten-Angabe (read-only)
 * neben dem editierbaren Berater-Feld und verdrahtet die Store-Aktionen.
 *
 * Datenquelle: Mandanten-Ebene = `dimensionData`, Berater-Ebene =
 * `beraterDimensionData` (siehe lib/store/wizard.ts). Resources (strukturiert)
 * ist hier bewusst NICHT enthalten — die hat einen eigenen Sub-Wizard.
 *
 * Die Feld-Schlüssel MÜSSEN mit denen übereinstimmen, die der Mandanten-Wizard
 * schreibt (`*_KEYS` aus lib/store/dimensionScoreInput.ts) — sonst bliebe die
 * read-only Mandanten-Spalte leer. Bewusst nur die Freitext-Narrativ-Felder je
 * Dimension: dort trägt die fachliche Bearbeitung (Textarea + Übernehmen +
 * Merge). Strukturierte Felder (Booleans, Enums, Zahlen, Listen) sind Fakten
 * des Mandanten, kein Berater-Narrativ, und werden hier nicht redigiert.
 */
const df = de.dimensionFields;

interface DimensionDef {
  readonly key: DimensionKey;
  readonly fields: readonly BeraterReviewField[];
  readonly sensitiveNote?: string;
}

const notesField: BeraterReviewField = {
  key: 'notes',
  label: df.common.notesLabel,
  hint: df.common.notesHint,
  placeholder: df.common.notesPlaceholder,
};

export const REVIEW_DIMENSIONS: readonly DimensionDef[] = [
  {
    key: 'identity',
    fields: [
      {
        key: IDENTITY_KEYS.usp,
        label: df.identity.uspLabel,
        hint: df.identity.uspHint,
        placeholder: df.identity.uspPlaceholder,
      },
      notesField,
    ],
  },
  {
    key: 'activity',
    fields: [
      {
        key: ACTIVITY_KEYS.valueProposition,
        label: df.activity.valuePropositionLabel,
        hint: df.activity.valuePropositionHint,
        placeholder: df.activity.valuePropositionPlaceholder,
      },
      {
        key: ACTIVITY_KEYS.targetCustomers,
        label: df.activity.targetCustomersLabel,
        hint: df.activity.targetCustomersHint,
      },
      notesField,
    ],
  },
  {
    key: 'style',
    fields: [
      {
        key: STYLE_KEYS.targetGroupDescription,
        label: df.style.targetGroupDescriptionLabel,
        hint: df.style.targetGroupDescriptionHint,
      },
      {
        key: STYLE_KEYS.differentiators,
        label: df.style.differentiatorsLabel,
        hint: df.style.differentiatorsHint,
      },
      notesField,
    ],
  },
  {
    key: 'health',
    sensitiveNote: df.health.sensitiveNote,
    fields: [
      {
        key: HEALTH_KEYS.copingStrategiesDescribed,
        label: df.health.copingLabel,
        hint: df.health.copingHint,
      },
      {
        key: HEALTH_KEYS.pastCrisisHandlingNotes,
        label: df.health.pastCrisisLabel,
        hint: df.health.pastCrisisHint,
      },
      notesField,
    ],
  },
  {
    key: 'private',
    sensitiveNote: df.private.sensitiveNote,
    fields: [
      {
        key: PRIVATE_KEYS.careResponsibilityDescription,
        label: df.private.careDescriptionLabel,
        hint: df.private.careDescriptionHint,
      },
      {
        key: PRIVATE_KEYS.zumutbarkeitsgruende,
        label: df.private.zumutbarkeitLabel,
        hint: df.private.zumutbarkeitHint,
      },
      {
        key: PRIVATE_KEYS.regionalLaborMarketContext,
        label: df.private.regionalContextLabel,
        hint: df.private.regionalContextHint,
      },
      notesField,
    ],
  },
] as const;

/**
 * Verdrahtet die Review-EINER Dimension an den Store (beide Ebenen) und
 * rendert das präsentationale `BeraterDimensionReview`. Wiederverwendet in der
 * gestapelten Cockpit-Sektion UND der Einzel-Dimensions-Deep-Link-Route.
 *
 * `detailHref` (optional) blendet im Kartenkopf einen Link zur Einzelansicht
 * ein — in der gestapelten Sektion gesetzt, in der Einzelansicht selbst nicht.
 */
export function ConnectedBeraterDimensionReview({
  caseId,
  dimension,
  detailHref,
}: {
  readonly caseId: string;
  readonly dimension: DimensionKey;
  readonly detailHref?: string;
}) {
  const mandant = useWizardStore((s) => s.cases[caseId]?.dimensionData[dimension]);
  const berater = useWizardStore((s) => s.cases[caseId]?.beraterDimensionData[dimension]);
  const setBeraterField = useWizardStore((s) => s.setBeraterDimensionField);
  const markReviewed = useWizardStore((s) => s.markBeraterDimensionReviewed);

  const def = REVIEW_DIMENSIONS.find((d) => d.key === dimension);
  if (!def || !mandant || !berater) return null;

  const reviewStatus = merge.mandantReviewStatus({
    mandantHasContent: merge.hasAnyContent(mandant.fields),
    mandantUpdatedAt: mandant.lastEditedAt,
    reviewedAt: berater.reviewedAt,
  });

  const headerAction = detailHref ? (
    <Link
      to={detailHref}
      className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500 underline-offset-4 hover:text-stone-900 hover:underline"
    >
      {de.beraterReview.openDetail} →
    </Link>
  ) : undefined;

  return (
    <BeraterDimensionReview
      dimensionLabel={de.dimensions[dimension].name}
      fields={def.fields}
      sensitiveNote={def.sensitiveNote}
      headerAction={headerAction}
      mandantFields={mandant.fields}
      beraterFields={berater.fields}
      reviewStatus={reviewStatus}
      mandantUpdatedAt={mandant.lastEditedAt}
      onBeraterChange={(fieldKey, value) => {
        setBeraterField(caseId, dimension, fieldKey, value);
      }}
      onAdopt={(fieldKey, value) => {
        setBeraterField(caseId, dimension, fieldKey, value);
      }}
      onMarkReviewed={() => {
        markReviewed(caseId, dimension);
      }}
    />
  );
}

export function BeraterDimensionReviewSection({ caseId }: { readonly caseId: string }) {
  const caseState = useWizardStore((s) => s.cases[caseId]);
  const ensureCase = useWizardStore((s) => s.ensureCase);

  useEffect(() => {
    if (!caseState) ensureCase(caseId);
  }, [caseState, ensureCase, caseId]);

  if (!caseState) return null;

  return (
    <section className="mb-10">
      <header className="mb-6">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.beraterReview.sectionLabel}
        </p>
        <h2 className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
          {de.beraterReview.sectionTitle}
        </h2>
        <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-500">
          {de.beraterReview.sectionIntro}
        </p>
      </header>

      <div className="space-y-6">
        {REVIEW_DIMENSIONS.map((d) => (
          <ConnectedBeraterDimensionReview
            key={d.key}
            caseId={caseId}
            dimension={d.key}
            detailHref={`/case/${caseId}/dimension/${d.key}`}
          />
        ))}
      </div>
    </section>
  );
}
